import { useMemo, useState } from 'react'
import type { CreateOrganizationRequest, FirstWeekResponse, Organization } from 'aeo-schema-generator-shared/types'
import { apiService } from '@/services/api'
import OrganizationForm from '@/components/OrganizationForm'
import { PROFILE_SAVE_FAILED } from './firstWeekCopy'

export type ProfilePrefill = FirstWeekResponse['profilePrefill']

/*
  Everything the guide needs to know about the business profile: whether one
  already covers this domain, what the scan can fill in, and how a save is
  written back with the domain attached. FirstWeekActions keeps the request
  protocol and the notices; this file keeps the profile.
*/

/**
 * The profile that already covers this domain, or null when there is none.
 * A null answer is the server saying "no profile yet". A throw is the
 * server failing, which is not the same thing and must not open a create
 * form: that is how a second profile gets made for one business.
 */
export async function findProfileForDomain(domain: string | null): Promise<Organization | null> {
  if (!domain) return null
  const res = await apiService.getOrganizationForDomain(domain)
  return res.data?.organization ?? null
}

export function FirstWeekProfileEditor({
  open,
  domain,
  existing,
  prefill,
  onClose,
  onSaved,
  onFailed,
}: {
  open: boolean
  domain: string | null
  /** The profile found by findProfileForDomain, or null to create a new one. */
  existing: Organization | null
  prefill: ProfilePrefill
  onClose: () => void
  onSaved: () => void
  onFailed: (message: string) => void
}) {
  const [saving, setSaving] = useState(false)

  /*
    What the scan learned, as the starting values of a new profile. Memoised
    because the form resets itself whenever this object changes identity.
  */
  const initialValues = useMemo<Partial<CreateOrganizationRequest> | undefined>(() => {
    if (!prefill) return undefined
    return {
      name: prefill.name ?? undefined,
      url: prefill.website ?? undefined,
      description: prefill.description ?? undefined,
      primaryCategory: prefill.primaryCategory ?? undefined,
      priorityTopics: prefill.priorityTopics ?? undefined,
    }
  }, [prefill])

  async function submitProfile(data: CreateOrganizationRequest) {
    setSaving(true)
    try {
      const payload: CreateOrganizationRequest = {
        ...data,
        associatedDomains: Array.from(new Set([...(data.associatedDomains ?? []), ...(domain ? [domain] : [])])),
      }
      if (existing) await apiService.updateOrganization(existing.id, payload)
      else await apiService.createOrganization(payload)
      onSaved()
    } catch {
      onFailed(PROFILE_SAVE_FAILED)
    } finally {
      setSaving(false)
    }
  }

  return (
    <OrganizationForm
      isOpen={open}
      onClose={onClose}
      onSubmit={submitProfile}
      organization={existing}
      initialValues={initialValues}
      isLoading={saving}
      defaultDomain={domain ?? undefined}
    />
  )
}
